import { Money } from "@hubrise/react-components"
import _ from "lodash"

import { OptionListId, ProductId, SkuId } from "shared/hubrise/ids"
import {
  Dimension,
  DimensionType,
  DimensionUnit,
  ICatalog,
  IProduct,
  ISku,
  isProduct,
  nutrientWeightUnits,
  volumeUnits,
  weightUnits,
} from "shared/hubrise/models"
import { replaceInArrayById, removeInArrayById, removeItemInArray, findById } from "shared/utils/javascript"

import { updatedCatalog } from "./Catalog"
import * as Deal from "./Deal"
import * as PriceOverrides from "./PriceOverrides"
import { sortedCategoriesAndProducts } from "./Product"
import * as Restrictions from "./Restrictions"
import { generateRefsForItems, GenerateRefsOptions, removeAllRefsFromItems } from "./ref"

/**
 * Return all skus in the catalog.
 * @param catalog
 */
export const allSkus = (catalog: ICatalog): Array<{ product: IProduct; sku: ISku }> => {
  return catalog.data.products.flatMap((product) => product.skus.map((sku) => ({ product, sku })))
}

/**
 * Return the sku matching the id.
 * @param catalog
 * @param id
 */
export const byId = (catalog: ICatalog, id: SkuId): ISku => findWithProductById(catalog, id).sku

/**
 * Return the sku matching the id, along with its parent product.
 * @param catalog
 * @param id
 */
export const findWithProductById = (catalog: ICatalog, id: SkuId): { product: IProduct; sku: ISku } => {
  for (const product of catalog.data.products) {
    const sku = findById(product.skus, id)
    if (sku) return { product, sku }
  }
  throw `Sku ${id} not found`
}

/**
 * Create an empty sku.
 */
export function create(catalog: ICatalog): ISku {
  return {
    id: SkuId(catalog.idGenerator()),
    ref: "",
    name: "",
    restrictions: { enabled: true },
    price: Money.zero(catalog.currency),
    priceOverrides: [],
    optionListIds: [],
    tags: [],
    barcodes: [],
    weight: null,
    volume: null,
    nbServings: null,
  }
}

/**
 * Delete the sku. The function name is because delete is a reserved JS word.
 * Throw an error if called on the only sku of a product.
 * @param catalog
 * @param productId
 * @param skuId
 */
export function doDelete(catalog: ICatalog, productId: ProductId, skuId: SkuId): ICatalog {
  return updatedCatalog(catalog, {
    products: replaceInArrayById(catalog.data.products, productId, (product) => {
      if (product.skus.length <= 1) throw `Cannot delete the only sku of a product: ${JSON.stringify(product)}`
      return {
        ...product,
        skus: removeInArrayById(product.skus, skuId),
      }
    }),
    deals: Deal.deleteSkuIds(catalog, [skuId]),
  })
}

/**
 * Detach the option list from the sku.
 * @param catalog
 * @param productId
 * @param skuId
 * @param optionListId
 */
export function detachOptionList(
  catalog: ICatalog,
  productId: ProductId,
  skuId: SkuId,
  optionListId: OptionListId,
): ICatalog {
  return updatedCatalog(catalog, {
    products: replaceInArrayById(catalog.data.products, productId, (product) => ({
      ...product,
      skus: replaceInArrayById(product.skus, skuId, (sku) => ({
        ...sku,
        optionListIds: removeItemInArray(sku.optionListIds, optionListId),
      })),
    })),
  })
}

/**
 * Returns the SKU full name, including product name. Eg. "Pizza Rosa Verde 10inch Classic"
 * @param product
 * @param sku
 */
export function fullName(product: IProduct, sku: ISku): string {
  return _.compact([product.name, sku.name]).join(" ")
}

/**
 * Returns true if the sku should be visible in <Preview>
 * @param sku
 * @param variantRef
 */
export function isVisibleInPreview(sku: ISku, variantRef: string | null): boolean {
  return Restrictions.isVisibleInPreview(sku.restrictions, variantRef)
}

/**
 * Returns the price of the sku for the given variant
 * @param sku
 * @param variantRef: the variant ref, or null for the catalog price
 */
export function price(sku: ISku, variantRef: string | null): Money {
  return variantRef === null ? sku.price : (variantPrice(sku, variantRef) ?? sku.price)
}

/**
 * Update a sku and return the resulting catalog.
 * @param catalog
 * @param productId
 * @param skuId
 * @param fieldName
 * @param value
 */
export function update<T extends keyof ISku>(
  catalog: ICatalog,
  productId: ProductId,
  skuId: SkuId,
  fieldName: T,
  value: ISku[T],
): ICatalog {
  return updatedCatalog(catalog, {
    products: replaceInArrayById(catalog.data.products, productId, (product) => ({
      ...product,
      skus: replaceInArrayById(product.skus, skuId, (sku) => ({ ...sku, [fieldName]: value })),
    })),
  })
}

/**
 * Remove refs from all SKUs.
 * @param catalog
 */
export function removeAllRefs(catalog: ICatalog): ICatalog {
  return updatedCatalog(catalog, {
    products: catalog.data.products.map((product) => ({
      ...product,
      skus: removeAllRefsFromItems(product.skus),
    })),
  })
}

/**
 * Generate refs for all SKUs, in the display order (sorted by category hierarchy).
 * @param catalog
 * @param options
 */
export function generateRefs(catalog: ICatalog, options: GenerateRefsOptions): ICatalog {
  const sortedProducts = sortedCategoriesAndProducts(catalog).filter(isProduct)

  const counter = { value: 1 }
  const updatedSkusByProductId = new Map<number, Array<ISku>>()
  for (const product of sortedProducts) {
    updatedSkusByProductId.set(product.id, generateRefsForItems(product.skus, options, "sku", counter))
  }

  return updatedCatalog(catalog, {
    products: catalog.data.products.map((product) => ({
      ...product,
      skus: updatedSkusByProductId.get(product.id)!,
    })),
  })
}

/**
 * Enable or disable the SKU. An optional variant ref can be provided.
 * @param catalog
 * @param productId
 * @param skuId
 * @param enabled
 * @param variantRef
 */
export function enable(
  catalog: ICatalog,
  productId: ProductId,
  skuId: SkuId,
  enabled: boolean,
  variantRef?: string,
): ICatalog {
  return updatedCatalog(catalog, {
    products: replaceInArrayById(catalog.data.products, productId, (product) => ({
      ...product,
      skus: replaceInArrayById(product.skus, skuId, (sku) => ({
        ...sku,
        restrictions:
          variantRef === undefined
            ? Restrictions.enable(sku.restrictions, enabled)
            : Restrictions.enableVariant(catalog.data.variants, sku.restrictions, variantRef, enabled),
      })),
    })),
  })
}

/**
 * Enable or disable all SKUs. An optional variant ref can be provided.
 * @param catalog
 * @param enabled
 * @param variantRef
 */
export function enableAll(catalog: ICatalog, enabled: boolean, variantRef?: string): ICatalog {
  return updatedCatalog(catalog, {
    products: catalog.data.products.map((product) => ({
      ...product,
      skus: product.skus.map((sku) => ({
        ...sku,
        restrictions:
          variantRef === undefined
            ? Restrictions.enable(sku.restrictions, enabled)
            : Restrictions.enableVariant(catalog.data.variants, sku.restrictions, variantRef, enabled),
      })),
    })),
  })
}

/**
 * Return the price override for the given variant, if any.
 * @param sku
 * @param variantRef
 */
export function variantPrice(sku: ISku, variantRef: string): Money | null {
  return PriceOverrides.variantPrice(sku.priceOverrides, variantRef)
}

/**
 * Set the price override for the given variant.
 * @param catalog
 * @param productId
 * @param skuId
 * @param variantRef
 * @param price
 */
export function setVariantPrice(
  catalog: ICatalog,
  productId: ProductId,
  skuId: SkuId,
  variantRef: string,
  price: Money | null,
): ICatalog {
  return updatedCatalog(catalog, {
    products: replaceInArrayById(catalog.data.products, productId, (product) => ({
      ...product,
      skus: replaceInArrayById(product.skus, skuId, (sku) => ({
        ...sku,
        priceOverrides: PriceOverrides.setVariantPrice(sku.priceOverrides, variantRef, price),
      })),
    })),
  })
}

/**
 * Turn a raw dimension input (e.g. "  150  cl ") into a normalised dimension (e.g. "150 cl").
 */

export function normaliseDimension<D extends Dimension>(
  raw: string,
  type: DimensionType<D>,
  defaultUnit: DimensionUnit<D>,
): D | null {
  const [, value, unit] =
    raw
      .trim()
      .replace(/µ/g, "u")
      .match(/([\d.]+)\s*([a-z]*)/) || []
  if (!value) return null

  const outValue = parseFloat(value)
  const outUnit = unit || defaultUnit

  const allowedUnits: ReadonlyArray<string> =
    type === "weight" ? weightUnits : type === "volume" ? volumeUnits : nutrientWeightUnits
  if (!allowedUnits.includes(outUnit)) {
    throw new Error(`Unit "${outUnit}" is not compatible with default unit "${defaultUnit}"`)
  }

  return `${outValue} ${outUnit}` as D
}
